'use client';

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { playerLootboxActivation } from '@/api/common';
import { TabNavigation } from '@/components/TabNavigation';
import { FlipWrapper } from '@/modules/common';
import {
  DAILY_LIMIT,
  EScratchTicketTheme,
  indexToTheme,
} from '@/modules/promotions';
import { useScratchGameLootbox } from '@/modules/promotions/hooks';
import { useGetCommonStore } from '@/store';
import { ScratchActionButton } from '../ScratchActionButton';
import { ScratchBonusTerms } from '../ScratchBonusTerms';
import { ScratchTicket } from './components/ScratchTicket';

import { ScScratchTicketSection } from './styled';

export const ScratchTicketSection = () => {
  const { t } = useTranslation('scratchGame');

  const useCommonStore = useGetCommonStore();

  const scratchGameMenuData = useCommonStore.use.scratchGameMenuData();
  const isMobileDevice = useCommonStore.use.isMobileDevice();

  const {
    lootbox,
    dailyActivatedLootboxes,
    isFinished,
    setIsFinished,
    ticketTheme,
    setTicketTheme,
    isAuthenticated,
    currentConfig,
    bonus,
    setBonus,
  } = useScratchGameLootbox();

  const handleComplete = useCallback(
    async (theme: EScratchTicketTheme) => {
      if (!lootbox[theme] || isFinished[theme]) return;

      try {
        setIsFinished(theme, true);

        const { data } = await playerLootboxActivation(lootbox[theme].id);
        if (data) {
          const isBasic = data.bonuses[0].title
            .toLowerCase()
            .includes(EScratchTicketTheme.BASIC);

          setBonus(
            isBasic ? EScratchTicketTheme.BASIC : EScratchTicketTheme.PREMIUM,
            data.bonuses[0]
          );
        }
      } catch (error) {
        console.log(error);
      }
    },
    [lootbox[ticketTheme], isFinished[ticketTheme]]
  );

  const TABS = ['Standard', <span className="premium-tab">Premium</span>];

  return (
    <ScScratchTicketSection>
      <div className="ticket-tabs">
        <TabNavigation
          maxWidth100
          menuItemWidth="50%"
          navItems={TABS}
          selectedItem={indexToTheme.indexOf(ticketTheme)}
          setSelectedItem={(index) => setTicketTheme(indexToTheme[index])}
          withoutShadow
        />
      </div>

      <p className="description">
        {t(
          `boxOffice.${isAuthenticated ? 'description' : 'descriptionUnlogged'}`,
          {
            minDep: currentConfig[ticketTheme].minDep,
            code: currentConfig[ticketTheme].code,
          }
        )}
      </p>

      <FlipWrapper
        back={
          <ScratchTicket
            bonus={bonus[EScratchTicketTheme.PREMIUM]}
            handleComplete={() => handleComplete(EScratchTicketTheme.PREMIUM)}
            isFinished={isFinished.premium}
            lootbox={lootbox.premium}
            theme={EScratchTicketTheme.PREMIUM}
          />
        }
        front={
          <ScratchTicket
            bonus={bonus[EScratchTicketTheme.BASIC]}
            handleComplete={() => handleComplete(EScratchTicketTheme.BASIC)}
            isFinished={isFinished.basic}
            lootbox={lootbox.basic}
            theme={EScratchTicketTheme.BASIC}
          />
        }
        height={isMobileDevice ? '203px' : '270px'}
        isFlipped={ticketTheme === EScratchTicketTheme.PREMIUM}
        width="397px"
      />

      <div className="scratch-daily-limit">
        <span>{scratchGameMenuData?.menu?.dailySpins}</span>
        <span>
          {dailyActivatedLootboxes}/{DAILY_LIMIT}
        </span>
      </div>

      <ScratchActionButton bonus={bonus[ticketTheme]} />

      {bonus[ticketTheme] && isFinished[ticketTheme] && isAuthenticated && (
        <ScratchBonusTerms bonus={bonus[ticketTheme]} />
      )}
    </ScScratchTicketSection>
  );
};
